import React from 'react';
import { FormGroup, InputGroup, Checkbox, Button, Intent } from '@blueprintjs/core';
import type { ReportParameter } from '../../types/reporting.js';
import { presetToDateRange } from '../DashboardPage/dashboardFilterHelpers.js';

interface ReportParameterBarProps {
  parameters: ReportParameter[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onApply: () => void;
}

const DATE_PRESETS: Array<{ value: string; label: string }> = [
  { value: 'all_time', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'past_7_days', label: 'Past 7 days' },
  { value: 'past_30_days', label: 'Past 30 days' },
  { value: 'past_90_days', label: 'Past 90 days' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
];

function dateRangePair(parameters: ReportParameter[]): { start: ReportParameter; end: ReportParameter } | null {
  const dateParams = parameters.filter((param) => {
    const dt = param.data_type.toLowerCase();
    return dt.includes('date') || dt.includes('timestamp');
  });
  const start = dateParams.find((param) => ['start_date', 'from_date', 'date_start'].includes(param.url_key) || param.url_key.includes('start'));
  const end = dateParams.find((param) => ['end_date', 'to_date', 'date_end'].includes(param.url_key) || param.url_key.includes('end'));
  return start && end ? { start, end } : null;
}

function presetForRange(start: string, end: string): string {
  if (start === '1900-01-01' && end === '2999-12-31') return 'all_time';
  for (const preset of DATE_PRESETS) {
    if (preset.value === 'custom') continue;
    const range = presetToDateRange(preset.value);
    if ((range.start ?? '') === start && (range.end ?? '') === end) return preset.value;
  }
  return 'custom';
}

function inputForParam(
  param: ReportParameter,
  value: string,
  onChange: (key: string, value: string) => void,
): React.ReactElement {
  const dt = param.data_type.toLowerCase();

  if (dt.includes('bool')) {
    return (
      <Checkbox
        checked={value === 'true'}
        onChange={(e) => onChange(param.url_key, (e.target as HTMLInputElement).checked ? 'true' : 'false')}
        label={param.ui_label ?? param.name}
      />
    );
  }

  if (dt.includes('date') || dt.includes('timestamp')) {
    return (
      <InputGroup
        type="date"
        value={value || ''}
        onChange={(e) => onChange(param.url_key, e.target.value)}
        placeholder={param.ui_label ?? param.name}
      />
    );
  }

  const type = dt.includes('int') || dt.includes('numeric') || dt.includes('float') ? 'number' : 'text';
  return (
    <InputGroup
      type={type}
      value={value}
      onChange={(e) => onChange(param.url_key, e.target.value)}
      placeholder={param.ui_label ?? param.name}
    />
  );
}

export function ReportParameterBar({ parameters, values, onChange, onApply }: ReportParameterBarProps): React.ReactElement {
  const sorted = [...parameters].sort((a, b) => a.ordinal_position - b.ordinal_position);
  const rangePair = dateRangePair(sorted);
  const skipIds = new Set(rangePair ? [rangePair.start.id, rangePair.end.id] : []);

  return (
    <div className="sky-report-param-bar">
      {rangePair && (
        <FormGroup
          label="Date Range"
          style={{ margin: 0 }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={presetForRange(values[rangePair.start.url_key] ?? '', values[rangePair.end.url_key] ?? '')}
              onChange={(e) => {
                const range = e.target.value === 'all_time'
                  ? { start: '1900-01-01', end: '2999-12-31' }
                  : presetToDateRange(e.target.value);
                onChange(rangePair.start.url_key, range.start ?? '');
                onChange(rangePair.end.url_key, range.end ?? '');
              }}
            >
              {DATE_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>{preset.label}</option>
              ))}
            </select>
            <InputGroup
              type="date"
              value={values[rangePair.start.url_key] ?? ''}
              onChange={(e) => onChange(rangePair.start.url_key, e.target.value)}
            />
            <InputGroup
              type="date"
              value={values[rangePair.end.url_key] ?? ''}
              onChange={(e) => onChange(rangePair.end.url_key, e.target.value)}
            />
          </div>
        </FormGroup>
      )}
      {sorted.filter((param) => !skipIds.has(param.id)).map((param) => (
        <FormGroup
          key={param.id}
          label={
            <>
              {param.ui_label ?? param.name}
              {param.is_required && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
            </>
          }
          style={{ margin: 0 }}
        >
          {inputForParam(param, values[param.url_key] ?? '', onChange)}
        </FormGroup>
      ))}
      <Button intent={Intent.PRIMARY} onClick={onApply}>
        Apply
      </Button>
    </div>
  );
}
